import { BinaryReader, BinaryWriter } from "@bufbuild/protobuf/wire";
import { Timestamp } from "../../google/protobuf/timestamp";
import { BenchmarkName } from "./benchmark_name";
export const protobufPackage = "snapchat.cdp.cof";
export interface BenchmarkValue {
    intValue?: number | undefined;
    longValue?: string | undefined;
    boolValue?: boolean | undefined;
    floatValue?: number | undefined;
}
export interface BenchmarkRequest {
    benchmarkNames: BenchmarkName[];
    expirationTimestamp: Date | undefined;
}
export interface BenchmarkResult {
    name: BenchmarkName;
    value: BenchmarkValue | undefined;
}
function createBaseBenchmarkValue(): BenchmarkValue {
    return { intValue: undefined, longValue: undefined, boolValue: undefined, floatValue: undefined };
}
export const BenchmarkValue: MessageFns<BenchmarkValue> = {
    encode(message: BenchmarkValue, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        if (message.intValue !== undefined) {
            writer.uint32(8).int32(message.intValue);
        }
        if (message.longValue !== undefined) {
            writer.uint32(16).int64(message.longValue);
        }
        if (message.boolValue !== undefined) {
            writer.uint32(24).bool(message.boolValue);
        }
        if (message.floatValue !== undefined) {
            writer.uint32(37).float(message.floatValue);
        }
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): BenchmarkValue {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseBenchmarkValue();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 1: {
                    if (tag !== 8) {
                        break;
                    }
                    message.intValue = reader.int32();
                    continue;
                }
                case 2: {
                    if (tag !== 16) {
                        break;
                    }
                    message.longValue = reader.int64().toString();
                    continue;
                }
                case 3: {
                    if (tag !== 24) {
                        break;
                    }
                    message.boolValue = reader.bool();
                    continue;
                }
                case 4: {
                    if (tag !== 37) {
                        break;
                    }
                    message.floatValue = reader.float();
                    continue;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<BenchmarkValue>, I>>(base?: I): BenchmarkValue {
        return BenchmarkValue.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<BenchmarkValue>, I>>(object: I): BenchmarkValue {
        const message = createBaseBenchmarkValue();
        message.intValue = object.intValue ?? undefined;
        message.longValue = object.longValue ?? undefined;
        message.boolValue = object.boolValue ?? undefined;
        message.floatValue = object.floatValue ?? undefined;
        return message;
    },
};
function createBaseBenchmarkRequest(): BenchmarkRequest {
    return { benchmarkNames: [], expirationTimestamp: undefined };
}
export const BenchmarkRequest: MessageFns<BenchmarkRequest> = {
    encode(message: BenchmarkRequest, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        writer.uint32(10).fork();
        for (const v of message.benchmarkNames) {
            writer.int32(v);
        }
        writer.join();
        if (message.expirationTimestamp !== undefined) {
            Timestamp.encode(toTimestamp(message.expirationTimestamp), writer.uint32(18).fork()).join();
        }
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): BenchmarkRequest {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseBenchmarkRequest();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 1: {
                    if (tag === 8) {
                        message.benchmarkNames.push(reader.int32() as any);
                        continue;
                    }
                    if (tag === 10) {
                        const end2 = reader.uint32() + reader.pos;
                        while (reader.pos < end2) {
                            message.benchmarkNames.push(reader.int32() as any);
                        }
                        continue;
                    }
                    break;
                }
                case 2: {
                    if (tag !== 18) {
                        break;
                    }
                    message.expirationTimestamp = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
                    continue;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<BenchmarkRequest>, I>>(base?: I): BenchmarkRequest {
        return BenchmarkRequest.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<BenchmarkRequest>, I>>(object: I): BenchmarkRequest {
        const message = createBaseBenchmarkRequest();
        message.benchmarkNames = object.benchmarkNames?.map((e) => e) || [];
        message.expirationTimestamp = object.expirationTimestamp ?? undefined;
        return message;
    },
};
function createBaseBenchmarkResult(): BenchmarkResult {
    return { name: 0, value: undefined };
}
export const BenchmarkResult: MessageFns<BenchmarkResult> = {
    encode(message: BenchmarkResult, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        if (message.name !== 0) {
            writer.uint32(8).int32(message.name);
        }
        if (message.value !== undefined) {
            BenchmarkValue.encode(message.value, writer.uint32(18).fork()).join();
        }
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): BenchmarkResult {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseBenchmarkResult();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 1: {
                    if (tag !== 8) {
                        break;
                    }
                    message.name = reader.int32() as any;
                    continue;
                }
                case 2: {
                    if (tag !== 18) {
                        break;
                    }
                    message.value = BenchmarkValue.decode(reader, reader.uint32());
                    continue;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<BenchmarkResult>, I>>(base?: I): BenchmarkResult {
        return BenchmarkResult.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<BenchmarkResult>, I>>(object: I): BenchmarkResult {
        const message = createBaseBenchmarkResult();
        message.name = object.name ?? 0;
        message.value = (object.value !== undefined && object.value !== null)
            ? BenchmarkValue.fromPartial(object.value)
            : undefined;
        return message;
    },
};
type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;
export type DeepPartial<T> = T extends Builtin ? T : T extends globalThis.Array<infer U> ? globalThis.Array<DeepPartial<U>> : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>> : T extends {} ? {
    [K in keyof T]?: DeepPartial<T[K]>;
} : Partial<T>;
type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P : P & {
    [K in keyof P]: Exact<P[K], I[K]>;
} & {
    [K in Exclude<keyof I, KeysOfUnion<P>>]: never;
};
function toTimestamp(date: Date): Timestamp {
    const seconds = Math.trunc(date.getTime() / 1000).toString();
    const nanos = (date.getTime() % 1000) * 1000000;
    return { seconds, nanos };
}
function fromTimestamp(t: Timestamp): Date {
    let millis = (globalThis.Number(t.seconds) || 0) * 1000;
    millis += (t.nanos || 0) / 1000000;
    return new globalThis.Date(millis);
}
export interface MessageFns<T> {
    encode(message: T, writer?: BinaryWriter): BinaryWriter;
    decode(input: BinaryReader | Uint8Array, length?: number): T;
    create<I extends Exact<DeepPartial<T>, I>>(base?: I): T;
    fromPartial<I extends Exact<DeepPartial<T>, I>>(object: I): T;
}
